import { Link } from 'react-router-dom';
import type { LeadListItem } from '../types';
import { StatusBadge, LeadScoreBadge } from './StatusBadge';
import { PROPERTY_TYPE_LABELS } from '../types';
import { formatDate } from '../utils/date';
import { PhoneNumber } from './PhoneNumber';

interface LeadCardProps {
  lead: LeadListItem;
}

export function LeadCard({ lead }: LeadCardProps) {
  const fullName = `${lead.owner_firstname} ${lead.owner_lastname}`;

  return (
    <Link
      to={`/lead/${lead.id}`}
      className="block bg-white rounded-lg shadow-sm border border-gray-200 p-4 hover:shadow-md hover:border-blue-300 transition-all"
    >
      <div className="flex items-start justify-between">
        <div className="flex-1 min-w-0">
          {/* Header with name and status */}
          <div className="flex items-center space-x-2 mb-2">
            <h3 className="text-lg font-semibold text-gray-900 truncate">{fullName}</h3>
            <StatusBadge status={lead.status} size="sm" />
          </div>
          
          {/* Contact info */}
          <div className="space-y-1 mb-3">
            <div className="flex items-center text-sm text-gray-600">
              <svg className="w-4 h-4 mr-1.5 text-gray-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
              </svg>
              <PhoneNumber phone={lead.owner_phone} status={lead.status} />
            </div>
            <div className="flex items-center text-sm text-gray-600">
              <svg className="w-4 h-4 mr-1.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <span className="truncate">{lead.prop_address}</span>
            </div>
          </div>
          
          {/* Property info row */}
          <div className="flex items-center space-x-4 text-sm">
            <span className="text-gray-500">
              {PROPERTY_TYPE_LABELS[lead.prop_type]}
            </span>
            <span className="text-gray-300">•</span>
            <span className="text-gray-500">
              {formatDate(lead.created_at)}
            </span>
          </div>
        </div>
        
        {/* Lead score */}
        <div className="flex-shrink-0 ml-4">
          <LeadScoreBadge score={lead.lead_score} />
        </div>
      </div>
      
      {/* Arrow indicator */}
      <div className="absolute right-4 top-1/2 -translate-y-1/2 opacity-0 group-hover:opacity-100 transition-opacity">
        <svg className="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </div>
    </Link>
  );
}
